import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CurrencyDollar, Check, Fire, Trophy, ArrowRight, 
  Play, Confetti, ArrowClockwise, ChatCircle, Target,
  Lightning, Calendar, CheckCircle, Circle
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlueprintPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [blueprint, setBlueprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPath, setSelectedPath] = useState(searchParams.get('path') || '');
  const [starting, setStarting] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  const pathOptions = [
    { value: 'affiliate_marketing', label: 'Affiliate Marketing', desc: 'Promote products, earn commissions. Low risk, quick start.', icon: '💰' },
    { value: 'ai_services', label: 'AI Services', desc: 'Sell AI-powered services. Content, images, automation.', icon: '🤖' },
    { value: 'digital_products', label: 'Digital Products', desc: 'Create once, sell forever. Guides, templates, courses.', icon: '📦' }
  ];

  // Fetch existing blueprint on mount
  useEffect(() => {
    const fetchBlueprint = async () => {
      try {
        const response = await axios.get(`${API}/blueprint/me`, {
          headers: getAuthHeader()
        });
        if (response.data) {
          setBlueprint(response.data);
          // Set expanded day to current day
          setExpandedDay(response.data.current_day || 1);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchBlueprint();
  }, [getAuthHeader]);

  const handleStartBlueprint = async () => {
    if (!selectedPath) {
      toast.error('Please select a path first');
      return;
    }

    setStarting(true);
    try {
      const response = await axios.post(`${API}/blueprint/start`, {
        path: selectedPath
      }, {
        headers: getAuthHeader()
      });
      setBlueprint(response.data);
      setExpandedDay(1);
      toast.success('Your 7-day blueprint is ready. Let\'s go!');
    } catch (error) {
      toast.error('Failed to start blueprint');
    } finally {
      setStarting(false);
    }
  };

  const handleToggleTask = async (taskId) => {
    try {
      const response = await axios.post(`${API}/blueprint/task/complete`, {
        task_id: taskId
      }, {
        headers: getAuthHeader()
      });
      setBlueprint(response.data);
      
      const task = response.data.plan.flatMap(d => d.tasks).find(t => t.id === taskId);
      if (task?.completed) {
        toast.success('Task completed! Keep going.');
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleComplete = async (madeMoney) => {
    try {
      await axios.post(`${API}/blueprint/complete`, {
        made_money: madeMoney
      }, {
        headers: getAuthHeader()
      });
      
      if (madeMoney) {
        toast.success('Congratulations! You did it! 🎉');
      } else {
        toast.success('You completed the blueprint. Keep pushing!');
      }
      
      setShowCompletion(false);
      // Refresh blueprint
      const response = await axios.get(`${API}/blueprint/me`, {
        headers: getAuthHeader()
      });
      setBlueprint(response.data);
    } catch (error) {
      toast.error('Failed to complete blueprint');
    }
  };

  const handleRestart = async () => {
    try {
      await axios.post(`${API}/blueprint/restart`, {}, {
        headers: getAuthHeader()
      });
      setBlueprint(null);
      setSelectedPath('');
      toast.success('Blueprint reset. Choose a new path!');
    } catch (error) {
      toast.error('Failed to restart');
    }
  };

  const handlePostProgress = () => {
    const day = blueprint?.current_day || 1;
    const progress = blueprint?.progress_percentage || 0;
    const path = pathOptions.find(p => p.value === blueprint?.selected_path)?.label || 'my path';
    
    const message = `Day ${day} of my First $100 Blueprint — ${Math.round(progress)}% complete on ${path}. Still building. 💪`;
    
    // Navigate to community with pre-filled post
    navigate('/community', { state: { prefillPost: message, section: 'accountability' } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#c9a227] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your blueprint...</p>
        </div>
      </div>
    );
  }

  // Path Selection Screen
  if (!blueprint) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <header className="bg-[#111] border-b border-[#222] sticky top-0 z-50">
          <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <Link to="/community" className="text-2xl font-black text-[#c9a227]">POWR</Link>
            <Link to="/resources" className="text-sm text-gray-400 hover:text-white">← Resources</Link>
          </div>
        </header>

        <main className="py-16 px-4 md:px-8">
          <div className="container mx-auto max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
              <div className="w-16 h-16 bg-[#c9a227]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <CurrencyDollar size={40} className="text-[#c9a227]" weight="duotone" />
              </div>
              <h1 className="text-4xl md:text-5xl font-black mb-4">
                First <span className="text-[#c9a227]">$100</span> Blueprint
              </h1>
              <p className="text-xl text-gray-400">
                7 days. Clear tasks. Real action. Your first money.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-[#111] border-[#222] p-8 rounded-xl mb-8">
                <h2 className="text-xl font-bold mb-6">Step 1: Choose Your Path</h2>
                
                <div className="space-y-4 mb-8">
                  {pathOptions.map((path) => (
                    <button
                      key={path.value}
                      onClick={() => setSelectedPath(path.value)}
                      className={`w-full p-5 border rounded-lg text-left transition-all flex items-start gap-4 ${
                        selectedPath === path.value 
                          ? 'border-[#c9a227] bg-[#c9a227]/10' 
                          : 'border-[#333] hover:border-[#444]'
                      }`}
                    >
                      <span className="text-3xl">{path.icon}</span>
                      <div>
                        <p className="font-bold text-lg">{path.label}</p>
                        <p className="text-gray-400">{path.desc}</p>
                      </div>
                      {selectedPath === path.value && (
                        <CheckCircle size={24} className="text-[#c9a227] ml-auto flex-shrink-0" weight="fill" />
                      )}
                    </button>
                  ))}
                </div>

                <Button 
                  onClick={handleStartBlueprint}
                  disabled={!selectedPath || starting}
                  className="w-full bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold rounded-lg h-14 text-lg disabled:opacity-50"
                >
                  {starting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                      Creating Your Blueprint...
                    </>
                  ) : (
                    <>
                      <Play size={20} className="mr-2" weight="fill" />
                      Start My 7-Day Blueprint
                    </>
                  )}
                </Button>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-center">
              <p className="text-gray-500 text-sm">
                Not sure which path? <Link to="/resources" className="text-[#c9a227] hover:underline">Get AI recommendations →</Link>
              </p>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  // Active Blueprint Screen
  const currentDay = blueprint.current_day || 1;
  const progress = blueprint.progress_percentage || 0;
  const streak = blueprint.streak || 0;
  const isCompleted = blueprint.completed;
  const pathLabel = pathOptions.find(p => p.value === blueprint.selected_path)?.label || blueprint.selected_path;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="bg-[#111] border-b border-[#222] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/community" className="text-2xl font-black text-[#c9a227]">POWR</Link>
          <div className="flex items-center gap-4">
            <Button onClick={handlePostProgress} size="sm" variant="outline" className="border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227] hover:text-black rounded-lg">
              <ChatCircle size={16} className="mr-1" /> Post Progress
            </Button>
            <Link to="/resources" className="text-sm text-gray-400 hover:text-white">Resources</Link>
          </div>
        </div>
      </header>

      <main className="py-8 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl">
          {/* Progress Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Card className="bg-gradient-to-br from-[#c9a227]/20 via-[#111] to-[#111] border-[#c9a227]/30 p-6 rounded-xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <p className="text-sm text-[#c9a227] font-mono uppercase tracking-wider mb-1">First $100 Blueprint</p>
                  <h1 className="text-2xl font-bold">{pathLabel}</h1>
                </div>
                <div className="flex items-center gap-4">
                  {streak > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/20 rounded-lg">
                      <Fire size={20} className="text-orange-400" weight="fill" />
                      <span className="font-bold text-orange-400">{streak} day streak</span>
                    </div>
                  )}
                  {isCompleted && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 rounded-lg">
                      <Trophy size={20} className="text-green-400" weight="fill" />
                      <span className="font-bold text-green-400">Completed!</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-gray-400">Day {currentDay} of 7</span>
                <span className="text-[#c9a227] font-mono">{Math.round(progress)}% complete</span>
              </div>
              <div className="h-3 bg-[#222] rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-[#c9a227] to-[#e5b82a]" 
                  initial={{ width: 0 }} 
                  animate={{ width: `${progress}%` }} 
                  transition={{ duration: 0.5 }}
                />
              </div>
            </Card>
          </motion.div>

          {/* Days List */}
          <div className="space-y-4 mb-8">
            {blueprint.plan.map((day, dayIndex) => {
              const dayTasks = day.tasks || [];
              const completedCount = dayTasks.filter(t => blueprint.completed_tasks?.includes(t.id)).length;
              const isDayComplete = completedCount === dayTasks.length;
              const isExpanded = expandedDay === day.day;
              const isCurrentDay = day.day === currentDay;

              return (
                <motion.div 
                  key={day.day}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: dayIndex * 0.05 }}
                >
                  <Card className={`bg-[#111] border rounded-lg overflow-hidden transition-all ${
                    isCurrentDay ? 'border-[#c9a227]/50' : isDayComplete ? 'border-green-500/30' : 'border-[#222]'
                  }`}>
                    <button
                      onClick={() => setExpandedDay(isExpanded ? null : day.day)}
                      className="w-full p-4 flex items-center justify-between hover:bg-[#1a1a1a] transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                          isDayComplete ? 'bg-green-500/20 text-green-400' : isCurrentDay ? 'bg-[#c9a227] text-black' : 'bg-[#222] text-gray-400'
                        }`}>
                          {isDayComplete ? <Check size={20} weight="bold" /> : day.day}
                        </div>
                        <div className="text-left">
                          <p className="font-bold">{day.title}</p>
                          <p className="text-sm text-gray-400">{day.focus}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">{completedCount}/{dayTasks.length}</span>
                        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }}>
                          <ArrowRight size={16} className="text-gray-400 rotate-90" />
                        </motion.div>
                      </div>
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="px-4 pb-4 space-y-3 border-t border-[#222] pt-4">
                            {dayTasks.map((task, taskIndex) => {
                              const isTaskComplete = blueprint.completed_tasks?.includes(task.id);
                              return (
                                <button
                                  key={task.id}
                                  onClick={() => handleToggleTask(task.id)}
                                  className={`w-full p-4 rounded-lg flex items-start gap-4 transition-all text-left ${
                                    isTaskComplete 
                                      ? 'bg-green-500/10 border border-green-500/30' 
                                      : 'bg-[#0a0a0a] border border-[#222] hover:border-[#333]'
                                  }`}
                                >
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                    isTaskComplete 
                                      ? 'bg-green-500 text-white' 
                                      : 'border-2 border-[#444]'
                                  }`}>
                                    {isTaskComplete && <Check size={14} weight="bold" />}
                                  </div>
                                  <div>
                                    <p className={`font-semibold ${isTaskComplete ? 'line-through text-gray-500' : ''}`}>
                                      {task.title}
                                    </p>
                                    <p className={`text-sm ${isTaskComplete ? 'text-gray-600' : 'text-gray-400'}`}>
                                      {task.description}
                                    </p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Completion Actions */}
          {progress >= 100 && !isCompleted && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <Card className="bg-gradient-to-br from-[#c9a227]/20 via-[#111] to-[#111] border-[#c9a227]/50 p-8 rounded-xl text-center">
                <Confetti size={48} className="text-[#c9a227] mx-auto mb-4" weight="duotone" />
                <h2 className="text-2xl font-bold mb-4">Blueprint Complete!</h2>
                <p className="text-gray-400 mb-6">You finished all 7 days. Now the big question...</p>
                <p className="text-xl font-bold mb-8">Did you make your first $100?</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button onClick={() => handleComplete(true)} className="bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg h-12 px-8">
                    <Trophy size={20} className="mr-2" /> Yes! I Made Money! 🎉
                  </Button>
                  <Button onClick={() => handleComplete(false)} variant="outline" className="border-[#333] text-gray-400 hover:text-white rounded-lg h-12 px-8">
                    Not Yet — Keep Going
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Completed State */}
          {isCompleted && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card className="bg-[#111] border-[#222] p-8 rounded-xl text-center">
                <Trophy size={48} className={`mx-auto mb-4 ${blueprint.made_money ? 'text-[#c9a227]' : 'text-gray-400'}`} weight="fill" />
                <h2 className="text-2xl font-bold mb-2">
                  {blueprint.made_money ? 'You Made Your First $100!' : 'Blueprint Completed'}
                </h2>
                <p className="text-gray-400 mb-6">
                  {blueprint.made_money 
                    ? 'This is just the beginning. Keep building, keep growing.' 
                    : 'You completed the work. Keep pushing — your breakthrough is coming.'}
                </p>
                <Button onClick={handleRestart} variant="outline" className="border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227] hover:text-black rounded-lg h-12 px-8">
                  <ArrowClockwise size={20} className="mr-2" /> Start a New Blueprint
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Action Buttons */}
          {!isCompleted && progress < 100 && (
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={handlePostProgress} className="flex-1 bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold rounded-lg h-12">
                <ChatCircle size={18} className="mr-2" /> Post My Progress
              </Button>
              <Button onClick={handleRestart} variant="outline" className="border-[#333] text-gray-400 hover:text-white rounded-lg h-12">
                <ArrowClockwise size={18} className="mr-2" /> Restart Blueprint
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlueprintPage;
